import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import NutMotif from "./NutMotif";

const STATS = [
  {
    value: 19,
    suffix: "+",
    display: null,
    pct: 1,
    label: "Years Manufacturing",
    sub: "Machining fasteners at Makarpura GIDC since 2007",
    accent: true,
  },
  {
    value: 9001,
    suffix: "",
    display: null,
    pct: 1,
    label: "ISO 9001:2015 Certified",
    sub: "Mill test certificates and batch traceability on every dispatch",
  },
  {
    value: 100,
    suffix: "",
    display: null,
    pct: 1,
    label: "Max Size M100 · 4 Inch",
    sub: "From M12 up to M100 and ¼\" to 4\", in any length required",
  },
  {
    value: 11,
    suffix: "+",
    display: null,
    pct: 0.92,
    label: "PSUs & Majors Supplied",
    sub: "IndianOil, BPCL, HPCL, CPCL, GAIL, BARC, NPCIL, FACT and more",
  },
];

const R = 54;
const C = 2 * Math.PI * R;

const Gauge = ({ stat, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView || stat.value == null) return;
    const controls = animate(0, stat.value, {
      duration: 1.7,
      delay: index * 0.12,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setN(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, index, stat.value]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -6 }}
      className="group relative bg-navy-deep py-10 lg:py-12 px-7 overflow-hidden"
      data-testid={`stat-${index + 1}`}
    >
      <span className="absolute inset-0 bg-gradient-to-br from-brand/0 via-brand/0 to-brand/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative w-[128px] h-[128px] mb-6">
        <svg viewBox="0 0 128 128" className="w-full h-full -rotate-90">
          <circle cx="64" cy="64" r={R} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="6" />
          <motion.circle
            cx="64"
            cy="64"
            r={R}
            fill="none"
            stroke="#ffffff"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={C}
            initial={{ strokeDashoffset: C }}
            whileInView={{ strokeDashoffset: C * (1 - stat.pct) }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1.7, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
          />
          {Array.from({ length: 24 }).map((_, i) => {
            const a = (i / 24) * Math.PI * 2;
            return (
              <line
                key={`tick-${a.toFixed(3)}`}
                x1={64 + Math.cos(a) * 41}
                y1={64 + Math.sin(a) * 41}
                x2={64 + Math.cos(a) * 45}
                y2={64 + Math.sin(a) * 45}
                stroke="rgba(255,255,255,0.22)"
                strokeWidth="1.5"
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display font-extrabold tabular-nums text-white text-[32px] leading-none">
            {n}
            <span className="text-base align-top ml-0.5">{stat.suffix}</span>
          </span>
        </div>
      </div>

      <div className="relative min-w-0">
        <div className="text-[11px] sm:text-xs tracking-[0.14em] uppercase text-white font-bold">
          {stat.label}
        </div>
        <div className="mt-2.5 text-xs sm:text-sm text-white/70 leading-relaxed max-w-[15rem]">
          {stat.sub}
        </div>
      </div>
    </motion.div>
  );
};

export default function Stats() {
  return (
    <section className="relative bg-navy-deep text-white overflow-hidden" data-testid="stats-section">
      <NutMotif
        size={620}
        color="#7FB2DC"
        opacity={0.05}
        className="-right-40 -top-40"
        duration={90}
      />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-12 lg:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-800 border border-slate-800">
          {STATS.map((s, i) => (
            <Gauge key={s.label} stat={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
